import React, { useMemo, useCallback, useState } from "react";
import Select, { components } from "react-select";
import { useDebounce } from "@utils/utils";
import ReactTooltip from "react-tooltip";

const SelectionCheckbox = React.memo(({ isSelected, disabled }) => <input className={`form-check-input company-contact-select ${disabled ? "custom-checkbox-unselect" : ""}`} type="checkbox" checked={isSelected} disabled={disabled} readOnly />);

SelectionCheckbox.displayName = "SelectionCheckbox";

const ParentOption = React.memo(
  ({ data, isSelected, ...props }) => {
    const stopPropagation = useCallback((e) => {
      e.stopPropagation();
    }, []);

    const showTooltip = data.isDisabled;

    return (
      <div onClick={stopPropagation}>
        <components.Option {...props} isSelected={isSelected}>
          <div className="d-flex justify-start align-items-center" data-tip={showTooltip ? "You can select maximum of 5 values." : ""} data-for="select-tooltip">
            <SelectionCheckbox isSelected={isSelected} disabled={data.isDisabled} />
            <label className="text-break m-0 mx-2 pl-1 pt-2 checboxselectlabel">{data.label}</label>
          </div>
        </components.Option>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.isSelected === nextProps.isSelected && prevProps.data.isDisabled === nextProps.data.isDisabled && prevProps.data.label === nextProps.data.label;
  }
);

ParentOption.displayName = "ParentOption";

const MenuList = React.memo(
  ({ children, ...props }) => (
    <components.MenuList {...props}>
      {children}
      <ReactTooltip id="select-tooltip" className="trademo-tooltip text-capitalize" backgroundColor="#8C8C8C" textColor="#FFFFFF" place="bottom" effect="solid" />
    </components.MenuList>
  ),
  (prevProps, nextProps) => prevProps.children === nextProps.children
);

MenuList.displayName = "MenuList";

const OptimizedSelect = ({ options: initialOptions, value, onChange, placeholder = "You can select up to 5 options", styles, ...props }) => {
  const [inputValue, setInputValue] = useState("");
  const debouncedInputValue = useDebounce(inputValue, 200);

  const selectedCount = useMemo(() => value?.length || 0, [value]);

  const handleBlur = () => {
    setInputValue("");
  };

  const isMaxSelected = useMemo(() => selectedCount >= 5, [selectedCount]);

  const processedOptions = useMemo(() => {
    if (!isMaxSelected) {
      return initialOptions.map((option) => ({ ...option, isDisabled: false }));
    }

    const selectedValues = new Set(value.map((v) => v.value));
    return initialOptions.map((option) => ({
      ...option,
      isDisabled: !selectedValues.has(option.value),
    }));
  }, [initialOptions, isMaxSelected, value]);

  const filteredOptions = useMemo(() => {
    if (!debouncedInputValue) return processedOptions;

    const searchStr = debouncedInputValue.toLowerCase();
    return processedOptions.filter((option) => option.label.toLowerCase().includes(searchStr));
  }, [debouncedInputValue, processedOptions]);

  const handleInputChange = useCallback((newValue, { action }) => {
    if (action === "input-change") {
      setInputValue(newValue);
    }
  }, []);

  const handleChange = useCallback(
    (newValue, { action }) => {
      if (!newValue) {
        onChange([]);
      } else if (Array.isArray(newValue) && newValue.length <= 5) {
        onChange(newValue);
      }
      if (action === "select-option" || action === "remove-value") {
        setInputValue("");
      }
    },
    [onChange]
  );

  const customComponents = useMemo(
    () => ({
      Option: ParentOption,
      MenuList,
    }),
    []
  );

  const customStyles = useMemo(
    () => ({
      ...styles,
      menuList: (base) => ({
        ...base,
        fontSize: "14px",
        fontWeight: 600,
        padding: "8px",
      }),
      option: (base, { isDisabled, isFocused, isSelected }) => ({
        ...base,
        backgroundColor: isDisabled ? null : isSelected ? "transparent" : isFocused ? "transparent" : null,
        color: isDisabled ? null : isSelected ? "#1a1722" : isFocused ? "#1a1722" : null,
        cursor: "pointer !important",
        marginBottom: "8px",
      }),
      control: (base, state) => ({
        ...base,
        minHeight: 54,
        backgroundColor: "#fff",
        borderRadius: "4px",
        fontSize: "14px",
        fontWeight: 600,
        border: state.isFocused ? "1px solid #6f08f1 !important" : "solid 1px #d0d5dd !important",
        boxShadow: state.isFocused ? "0 0 0 2px #e4dbfa !important" : "none !important",
      }),
    }),
    [styles]
  );

  return <Select options={filteredOptions} filterOption={null} onBlur={handleBlur} styles={customStyles} value={value} onChange={handleChange} onInputChange={handleInputChange} inputValue={inputValue} placeholder={placeholder} isMulti closeMenuOnSelect={false} hideSelectedOptions={false} components={customComponents} isSearchable backspaceRemovesValue blurInputOnSelect={false} captureMenuScroll={false} closeMenuOnScroll={false} maxMenuHeight={300} menuPlacement="auto" {...props} />;
};

export default React.memo(OptimizedSelect);
